import { useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { skipHiddenPagePoll } from "@/shared/utils/skipHiddenPagePoll";
import { SupportRequestsOpenCountQuery } from "../graphql/queries";
import type { SupportRequestsOpenCountQueryType } from "@/types/graphql/partner.generated";

/** Опрос раз в минуту: обращение может прийти, пока кабинет открыт. */
export const OPEN_REQUESTS_POLL_MS = 60_000;

/**
 * Число обращений, ждущих ответа команды (OPEN и VIEWER_REPLIED), текущей
 * организации для
 * счётчика в меню и карточки дашборда. Без права read SupportRequest
 * вызывающий передаёт skip: запрос не уходит вовсе. Тик опроса в скрытой
 * вкладке пропускается, при переходе между страницами счётчик освежается.
 *
 * Опрашивает только Layout (меню). Остальные места (карточка дашборда)
 * передают poll: false и читают тот же кеш: без своего опроса и
 * перезапроса по адресу, сеть только если в кеше ещё пусто.
 */
export function useSupportRequestsOpenCount({
  skip = false,
  poll = true,
}: { skip?: boolean; poll?: boolean } = {}): {
  count: number;
  /** Ответ уже пришёл: до него ноль ещё не значит «обращений нет». */
  loaded: boolean;
  refetch: () => void;
} {
  const { pathname } = useLocation();
  const { data, refetch } = useQuery<SupportRequestsOpenCountQueryType>(
    SupportRequestsOpenCountQuery,
    {
      skip,
      pollInterval: skip || !poll ? 0 : OPEN_REQUESTS_POLL_MS,
      skipPollAttempt: skipHiddenPagePoll,
      fetchPolicy: poll ? "cache-and-network" : "cache-first",
    }
  );

  const refresh = () => {
    if (!skip) void refetch();
  };

  // Только смена адреса: первый запрос уходит сам из useQuery.
  const lastPathname = useRef(pathname);
  useEffect(() => {
    if (!poll || lastPathname.current === pathname) return;
    lastPathname.current = pathname;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return {
    count: skip ? 0 : (data?.supportRequestsOpenCount ?? 0),
    loaded: !skip && data !== undefined,
    refetch: refresh,
  };
}
